import { Form, FormPage } from '../models/Form'
import { Employee } from '../models/Employee'
import { CampaignData, CampaignEmployee, isEmployee } from '../models/Campaign'
import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useAuth } from '../utils/auth/Auth'
import { FormInstance, useForm } from 'antd/es/form/Form'

type FormContextProps = {
  campaignEmployee: CampaignEmployee | undefined
  form: Form | undefined
  employee: Employee | undefined
  manager: Employee | undefined
  isEmployee: boolean
  isManager: boolean
  isFulfilled: boolean
  isSigned: boolean
  steps: { title: string; page: FormPage }[]

  // ANTD
  formInstance: FormInstance | undefined

  // Navigation
  activeTab: number
  setActiveTab: React.Dispatch<React.SetStateAction<number>>
}

const initFormContextPropsState: FormContextProps = {
  campaignEmployee: undefined,
  form: undefined,
  employee: undefined,
  manager: undefined,
  isEmployee: false,
  isManager: false,
  isFulfilled: false,
  isSigned: false,
  steps: [],

  formInstance: undefined,

  activeTab: 0,
  setActiveTab: () => {},
}

const FormContext = createContext<FormContextProps>(initFormContextPropsState)

const useFormEditor = () => useContext(FormContext)

interface FormContextProviderProps {
  campaignData: CampaignData
}

const FormContextProvider = ({
  campaignData,
  children,
}: PropsWithChildren<FormContextProviderProps>) => {
  const form = campaignData.form
  const campaignEmployee = campaignData.campaignEmployee

  const { currentUser } = useAuth()

  const [formInstance] = useForm()
  const [fillerIsEmployee, setFillerIsEmployee] = useState<boolean>(true)
  const [isFulfilled, setIsFulfilled] = useState<boolean>(false)
  const [isSigned, setIsSigned] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState<number>(0)

  // Loading ensure the children components have the right infos
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const steps = (form.pages ?? []).map((page: FormPage, index: number) => ({
    title: page.name,
    page: page,
  }))

  useEffect(() => {
    setIsLoading(true)
    const nextFillerIsEmployee = isEmployee(currentUser!, campaignEmployee!)
    setFillerIsEmployee(nextFillerIsEmployee)
    setIsFulfilled(
      (nextFillerIsEmployee
        ? campaignEmployee?.employeeFulfilledAt
        : campaignEmployee?.managerFulfilledAt) != undefined
    )
    setIsSigned(
      (nextFillerIsEmployee
        ? campaignEmployee?.employeeSignedAt
        : campaignEmployee?.managerSignedAt) != undefined
    )
    setIsLoading(false)
  }, [campaignData])

  return (
    <FormContext.Provider
      value={{
        campaignEmployee: campaignEmployee,
        form: form,
        employee: campaignEmployee?.employee,
        manager: campaignEmployee?.manager,
        isEmployee: fillerIsEmployee,
        isManager: !fillerIsEmployee,
        isFulfilled,
        isSigned,
        steps,
        formInstance,
        activeTab,
        setActiveTab,
      }}
    >
      {!isLoading && children}
    </FormContext.Provider>
  )
}

export { FormContextProvider, useFormEditor }
